import { sum } from 'es-toolkit';
import React, { FunctionComponent, useRef } from 'react';
import { Box, ListItemText, Paper } from '@mui/material';

import { ComponentDesign, GroupedProductionComponentInstance } from '@paragon/contract-library';
import {
  CarriedComponentsSchema,
  DataRow,
  getQuantityMap,
  isContainerRow,
  isDataRow,
  ModelChildLoader,
  Row,
} from '@paragon/layout-models';
import { forceArray } from '@paragon/utilities';

import { getActiveComponentDesignGuid } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { isLayoutViewActive } from '../../../../store/selectors/layoutModeSelectors';
import { ComponentPreview } from '../../../controls/ComponentPreview';
import { useAppSelector } from '../../../hooks';
import { addIdentifierToDataRowName } from './name-decorators/identifier';
import { addQuantityToDataRowName } from './name-decorators/quantity';
import PrimaryRowText from './name-formatters/PrimaryRowText';
import { SecondaryRowText } from './name-formatters/SecondaryRowText';
import { EndRowButtons } from './row-buttons';
import { isComponentDesignRow } from './schemas/CustomTrussDesignSchema';
import { SelectedComponentRowAdditionalInformation } from './SelectedComponentRowAdditionalInformation';

interface Props {
  row: Row;
  loader: ModelChildLoader;
}

export function isComponentDesignLikeRow(
  row: Row,
): row is DataRow<ComponentDesign | GroupedProductionComponentInstance> {
  return isComponentDesignRow(row) || (isDataRow(row) && row.type === 'groupedProductionComponentInstance');
}

export const NavigationPanelRowValue: FunctionComponent<Props> = ({ loader, row }) => {
  const quantityMap = useAppSelector(getQuantityMap);
  const displayComponentDesignAdditionalInfo = useAppSelector(
    (state) => !isLayoutViewActive(state) && getActiveComponentDesignGuid(state) === row.rowId,
  );

  const rowRef = useRef<HTMLSpanElement>(null);

  let rowName = row.name;
  if (isContainerRow(row) && row.childrenType === 'componentDesign') {
    rowName = `Components (${row.childrenCount})`;
  } else if (isContainerRow(row) && row.parent && row.childrenType === 'jackGirderConnection') {
    const allChildren = forceArray(
      loader.getChildrenOf((row.parent as DataRow<ComponentDesign>).item, CarriedComponentsSchema),
    );
    rowName = `Connections (${sum(allChildren.map((carriedComponentConnection) => carriedComponentConnection.numberOfJacks))})`;
  }

  addQuantityToDataRowName(rowName, row, quantityMap);
  addIdentifierToDataRowName(rowName, row, loader);

  const standardRowContent = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',

        userSelect: 'none',
      }}
      ref={rowRef}
    >
      <ListItemText
        primary={<PrimaryRowText row={row} displayName={rowName} />}
        secondary={<SecondaryRowText row={row} />}
      />

      {isComponentDesignLikeRow(row) ? (
        <InlineRowComponentPreview row={row} />
      ) : (
        <Box sx={{ mx: 2 }}>
          <EndRowButtons row={row} />
        </Box>
      )}
    </Box>
  );

  let additionalContentForSelectedRow = null;

  if (isComponentDesignLikeRow(row) && displayComponentDesignAdditionalInfo) {
    additionalContentForSelectedRow = <SelectedComponentRowAdditionalInformation />;
  }

  if (additionalContentForSelectedRow) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        {standardRowContent}
        {additionalContentForSelectedRow}
      </Box>
    );
  }

  return standardRowContent;
};

function InlineRowComponentPreview(props: { row: DataRow<ComponentDesign | GroupedProductionComponentInstance> }) {
  const analysisSetGuidMap = useAppSelector((state) => state.productionModels.analysisSetGuidToComponentDesignGuid);

  let componentDesignGuid;
  if (isComponentDesignRow(props.row)) {
    componentDesignGuid = props.row.item.guid;
  } else if (props.row.type === 'groupedProductionComponentInstance') {
    componentDesignGuid = analysisSetGuidMap[(props.row.item as GroupedProductionComponentInstance).analysisSetGuid];
  } else {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ margin: 1, padding: 1 }}>
      <ComponentPreview componentDesignGuid={componentDesignGuid} maxWidth={115} maxHeight={52} />
    </Paper>
  );
}
